import { useEffect, useMemo, useState } from "react";
import { Checker, GridBoard, type GridCell } from "./GridBoard.js";
import { haptic } from "../lib/telegram.js";
import type { CheckersMove, CheckersView } from "../lib/views.js";

interface Props {
  view: CheckersView;
  onMove(move: CheckersMove): void;
}

/**
 * Доска шашек.
 *
 * Цепочка взятий вводится по шагам: тапаешь свою шашку, потом поля приземления
 * одно за другим. Показываем только те продолжения, что ведут к настоящему
 * легальному ходу, — оборвать цепочку на середине физически невозможно.
 */
export function CheckersBoard({ view, onMove }: Props) {
  /** Уже набранная часть пути: [откуда, ...приземления]. */
  const [path, setPath] = useState<number[]>([]);

  const myTurn = view.turn === view.me;
  useEffect(() => {
    setPath([]);
  }, [view.lastPath.join(","), view.turn]);

  /** Ходы, начало которых совпадает с уже набранным путём. */
  const matching = useMemo(
    () => view.legalMoves.filter((m) => path.every((p, i) => m.path[i] === p)),
    [view.legalMoves, path],
  );

  /** Куда можно пойти следующим шагом. */
  const nextSteps = useMemo(() => {
    const set = new Set<number>();
    for (const m of matching) {
      const step = m.path[path.length];
      if (step !== undefined) set.add(step);
    }
    return set;
  }, [matching, path]);

  /** Шашки, которыми вообще можно начать ход. */
  const origins = useMemo(
    () => new Set(view.legalMoves.map((m) => m.path[0]!)),
    [view.legalMoves],
  );

  function tap(index: number) {
    if (!myTurn) return;

    // Новое начало хода.
    if (path.length === 0) {
      if (!origins.has(index)) return;
      haptic.select();
      setPath([index]);
      return;
    }

    // Тап по своей же шашке — начать заново.
    if (index === path[0] && path.length === 1) {
      setPath([]);
      return;
    }
    if (!nextSteps.has(index)) {
      // Тап мимо: если это другая своя шашка — переключаемся на неё.
      if (origins.has(index)) {
        haptic.select();
        setPath([index]);
      }
      return;
    }

    const next = [...path, index];
    // Ход завершён ровно тогда, когда путь совпал с легальным целиком.
    const done = view.legalMoves.find(
      (m) => m.path.length === next.length && m.path.every((p, i) => p === next[i]),
    );
    if (done) {
      haptic.move();
      onMove({ path: next });
      setPath([]);
      return;
    }
    haptic.tap();
    setPath(next);
  }

  const cells: GridCell[] = view.board.map((piece, i) => {
    const x = i % view.size;
    const y = Math.floor(i / view.size);
    const dark = (x + y) % 2 === 1;
    const side = piece === 0 ? null : piece <= 2 ? 1 : 2;
    const king = piece === 2 || piece === 4;

    return {
      dark,
      ...(side ? { content: <Checker side={side as 1 | 2} king={king} /> } : {}),
      playable: myTurn && (path.length === 0 ? origins.has(i) : nextSteps.has(i)),
      selected: path.includes(i),
      lastMove: view.lastPath.includes(i),
    };
  });

  return (
    <div className="stack" style={{ gap: 8 }}>
      <GridBoard
        size={view.size}
        cells={cells}
        flipped={view.mySide === 2}
        onCell={tap}
      />
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="muted small">
          {view.count.mine} : {view.count.theirs}
        </span>
        {myTurn && view.mustCapture && (
          <span className="small" style={{ color: "var(--gold)", fontWeight: 600 }}>
            бить обязательно
          </span>
        )}
      </div>
    </div>
  );
}
